import { useSelector } from 'react-redux';
import useCurrentUserId from './useCurrentUserId';

/**
 * A display name for an agent id, resolved locally.
 *
 * Messages carry `sentBy` (an id) and typing events carry `userId`; neither
 * carries a name, by design — socket payloads are identifiers only.
 *
 * ★ THE THREAD NOW BRINGS ITS OWN CAST, AND IT IS THE ONLY DEPENDABLE SOURCE.
 * The order below used to end at the Redux users slice, which is filled by the
 * users ADMIN page and nothing else — so on the inbox it was almost always
 * empty and every bubble from a colleague read as the literal word "Agent". A
 * supervisor reviewing a shared thread could not tell two of their own people
 * apart, which is the entire reason for reviewing it. `GET .../messages` now
 * answers with a `participants` map for exactly the senders on the page, and
 * that map is consulted before the slice.
 *
 * Order: "You" for the viewer, the thread's own participants, the conversation
 * owner, the users slice, then "Agent". Never a fabricated name.
 */
const fullName = (u) =>
	[u?.firstName, u?.lastName].filter(Boolean).join(' ') || u?.username || u?.name || null;

export function useAgentNames(conversation, participants = null) {
	const me = useCurrentUserId();
	const allUsers = useSelector((state) => state?.users?.allUsers) ?? [];

	return (userId) => {
		if (!userId) return 'Agent';
		const id = String(userId);
		if (me && id === me) return 'You';
		if (participants?.[id]?.name) return participants[id].name;
		if (conversation?.ownerUser?._id && String(conversation.ownerUser._id) === id) {
			return conversation.ownerUser.name || 'Agent';
		}
		const found = allUsers.find((u) => String(u?._id) === id);
		return fullName(found) || 'Agent';
	};
}

/**
 * The same resolution, for the PICTURE rather than the name.
 *
 * ★ SEPARATE FROM THE NAME, BECAUSE THE ANSWERS DIFFER.
 * "You" is a name the viewer has no avatar for, and an agent with no uploaded
 * photo has a name and no image at all. Returning null here is the normal case,
 * not a failure — the bubble draws coloured initials from the name instead, and
 * an avatar that falls back to initials is the same shape either way.
 *
 * The value is a PATH as stored on the user, not a URL. The caller prefixes it
 * with the API base, the way every other avatar in this CRM is built.
 */
export function useAgentAvatars(participants = null) {
	return (userId) => {
		if (!userId) return null;
		return participants?.[String(userId)]?.profileImage ?? null;
	};
}

export default function useAgentName(userId, conversation, participants = null) {
	const resolve = useAgentNames(conversation, participants);
	return resolve(userId);
}
